/**
 * MatchHeader — the event page's header.
 *
 * Pass 2 rebuilds it on the showroom's `EventHeader`
 * (`atlas-universe/src/frames/operator/EventHeader`), which was itself built
 * from two real Danish event pages. Three things every one of them has and
 * this did not:
 *
 *   - A BACK ARROW. A page you cannot go back from is a screenshot.
 *   - A FAVOURITE STAR. Every real book has one.
 *   - THE COMPETITION AS THE TITLE, on its own 44px row, rather than as a
 *     meta line above two crests.
 *
 * And one thing it gains: the fixture's own ground behind the header, at
 * opacity .55 under a top-to-bottom mask, so every event page has its own
 * identity without a crest in sight — and stays quiet enough that the book
 * below is still the loudest thing on the page.
 *
 * SMALL, because both references are. The old header was a centred block
 * with two 56px crests and a "MOD" between them, which made the header the
 * subject of a page whose subject is the market.
 *
 * The crests are gone on purpose: the brands ship `--team-visual: none`, and
 * the two club names in display type say the same thing in less height.
 *
 * Locale-aware: the kickoff time and date use `market.locale` /
 * `market.timezone`, so the display always reads in the league's own time.
 */

import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/index.js';
import { useTenantConfig } from '../../tenant/useTenantConfig.js';
import { formatKickoffTime, kickoffDateShort } from '../../utils/datetime.js';
import type { MatchDetailMatch } from '@aamp/shared-types';
import styles from './MatchHeader.module.css';

interface MatchHeaderProps {
  match: MatchDetailMatch;
  /**
   * The fixture's own ground, behind the header. The engine ships it on the
   * match-detail article (`article.heroImage`); absent → the header renders
   * on the flat card colour and says nothing about it.
   */
  image?: string | null;
}

function leagueLabel(league: MatchDetailMatch['league']): string {
  if (!league) return '';
  if (typeof league === 'string') return league;
  return league.name ?? '';
}

export function MatchHeader({ match, image }: MatchHeaderProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tenant = useTenantConfig();
  const market =
    tenant.kind === 'live' || tenant.kind === 'paused'
      ? tenant.config.market
      : null;
  const locale = market?.locale ?? 'en-GB';
  const timezone = market?.timezone ?? 'UTC';

  const kickoffDate = kickoffDateShort(match.kickoff, locale, timezone);
  const kickoffTime = formatKickoffTime(match.kickoff, locale, timezone);
  const league = leagueLabel(match.league);

  const goBack = (): void => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      void navigate(-1);
      return;
    }
    void navigate('/');
  };

  return (
    <header className={styles.header}>
      {image ? (
        <img
          className={styles.photo}
          src={image}
          alt=""
          loading="lazy"
          decoding="async"
          onError={(e): void => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      ) : null}

      <div className={styles.bar}>
        <button
          type="button"
          className={styles.icon}
          aria-label={t('common.back', { defaultValue: 'Back' })}
          onClick={goBack}
        >
          <BackGlyph />
        </button>
        <span className={styles.competition}>{league}</span>
        <button
          type="button"
          className={styles.icon}
          aria-label={t('common.addFavourite', { defaultValue: 'Add to favourites' })}
          aria-pressed={false}
        >
          <StarGlyph />
        </button>
      </div>

      <div className={styles.teams}>
        <span className={styles.side}>
          <span className={styles.name}>{match.homeTeam.name}</span>
        </span>
        <span className={styles.centre}>
          {kickoffTime ? <span className={styles.when}>{kickoffTime}</span> : null}
          {kickoffDate ? <span className={styles.state}>{kickoffDate}</span> : null}
        </span>
        <span className={styles.side}>
          <span className={styles.name}>{match.awayTeam.name}</span>
        </span>
      </div>

      {match.venue ? <div className={styles.venue}>{match.venue}</div> : null}
    </header>
  );
}

const glyph = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  width: 18,
  height: 18,
  'aria-hidden': true as const,
};

function BackGlyph(): ReactElement {
  return (
    <svg {...glyph}>
      <path d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function StarGlyph(): ReactElement {
  return (
    <svg {...glyph}>
      <path d="M12 3.5l2.6 5.6 6 .8-4.4 4.2 1.1 6-5.3-2.9-5.3 2.9 1.1-6L3.4 9.9l6-.8z" />
    </svg>
  );
}

export default MatchHeader;
